import { Component, OnInit, inject, input } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {select, Store} from '@ngrx/store';
import { faCog, faPhone, faUserHelmetSafety, faBars, faSignOut, faGlobe } from '@fortawesome/pro-light-svg-icons'; // 'fal' (Light version)

import { map, Observable, of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AnamneseConfirmLeaveDialogComponent, ChangeAddressDialogComponent } from "libs/ux-components/src/lib/components";
import { AuthState } from 'libs/auth/src/lib/store/auth.reducer';
import { selectAdminUsername, selectDisplayName, selectInternalUserRoles } from 'libs/auth/src/lib/store/auth.selectors';
import { LogoutAction } from 'libs/auth/src/lib/store/auth.actions';
import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import { selectCurrentUrl } from 'libs/tm-common/src/lib/store/selectors/selectors';
import { GoAction } from 'libs/tm-common/src/lib/store/actions/actions';
import { NgClass, AsyncPipe, UpperCasePipe, NgTemplateOutlet } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { MatButton, MatAnchor } from '@angular/material/button';
import { MatMenuTrigger, MatMenuModule } from '@angular/material/menu';
import { NgxLoadingBar } from '@ngx-loading-bar/core';
import { InternalUserRole } from 'libs/tm-common/src/lib/models/user.model';
import { Router } from '@angular/router';
import { MaintenanceService } from 'libs/maintenance/src/lib/services/maintenance.lib.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ViewportStore } from 'libs/tm-common/src/lib/store/viewport.store';
import { ClientName } from 'libs/config/client.names';

export const CHANGE_ADDRESS_TARGET = "changeAddressDialog"

export type PageHeaderMenuEntry = {title: string, route?: string, url?: string, roles?: InternalUserRole[]};

@Component({
    selector: 'lib-page-header',
    templateUrl: './page-header.component.html',
    styleUrls: ['./page-header.component.scss'],
	imports: [
		NgClass,
		FaIconComponent,
		MatButton,
		MatMenuTrigger,
		MatMenuModule,
		MatAnchor,
		NgxLoadingBar,
		AsyncPipe,
		TranslatePipe,
		UpperCasePipe,
		MatTooltipModule,
		NgTemplateOutlet,
	],
})
export class PageHeaderComponent implements OnInit {
	protected config = inject<AppConfig>(APP_CONFIG_TOKEN);
	protected translate = inject(TranslateService);
	private maintenanceService = inject(MaintenanceService);
	private dialog = inject(MatDialog);
	private store = inject<Store<AuthState>>(Store);
	protected viewportStore = inject(ViewportStore);
	private router = inject(Router);

	faCog = faCog;
	faSignOut = faSignOut;
	faBars = faBars;
	faGlobe = faGlobe;
	faPhone = faPhone;
	faUserHelmetSafety = faUserHelmetSafety;

	ClientName = ClientName;

	readonly menuEntries = input<PageHeaderMenuEntry[]>([]);
	readonly subMenuEntries = input<PageHeaderMenuEntry[]>([]);
	readonly adminMenuEntries = input<PageHeaderMenuEntry[]>([]);
	readonly isEtrackerInitialized = input<boolean>(false);
	readonly title = input<string>('TREATMENT MANAGER');

	timeToMaintenance$: Observable<string | null>;

	url$: Observable<string>;
	adminDisplayName$: Observable<string>;
	adminUsername$: Observable<string>;
	internalUserRoles$: Observable<InternalUserRole[]>;
	isAdminMenuVisible$: Observable<boolean>;

	ngOnInit(): void {
		this.url$ = this.store.pipe(select(selectCurrentUrl));
		this.internalUserRoles$ = this.store.pipe(select(selectInternalUserRoles));
		this.adminDisplayName$ = this.store.pipe(select(selectDisplayName));
		this.adminUsername$ = this.store.pipe(select(selectAdminUsername));

		this.isAdminMenuVisible$ = this.internalUserRoles$.pipe(
			map(roles => roles &&
				[InternalUserRole.ADMIN, InternalUserRole.SALES_MANAGER].some(role => roles.includes(role))
			)
		);

		this.timeToMaintenance$ = this.maintenanceService.getTimeToMaintenanceCountdown();
	}

	private navigateTo(target: string) {
		if (target === CHANGE_ADDRESS_TARGET) {
			this.dialog.open(ChangeAddressDialogComponent);
		} else {
			this.store.dispatch(new GoAction({ path: [target] }));
		}
	}

	signout() {
		if (this.router.url?.includes("/anamnese")) {
			const dialogRef = this.dialog.open(AnamneseConfirmLeaveDialogComponent);
			dialogRef.afterClosed().subscribe((result) => {
				if (result === "yes") {
					this.store.dispatch(new LogoutAction());
				}
			});
		} else {
			this.store.dispatch(new LogoutAction());
		}
	}

	switchLanguage(language: string) {
		this.translate.use(language);
	}

	showMenuContents(url: string): boolean{
		// remove potential query params
		const basePath = url.split('?')[0];
		return !['/signin', '/register', '/error'].includes(basePath);
	}

	/**
	 * Checks if an admin menu entry should be visible based on the user roles
	 */
	isAdminMenuEntryVisible(entry: PageHeaderMenuEntry): Observable<boolean> {
		// if no roles are defined, the entry is always visible
		if (!entry.roles || entry.roles.length === 0) {
			return of(true);
		}

		// check if the user has at least one of the required roles
		return this.internalUserRoles$.pipe(
			map(roles => roles && entry.roles.some(requiredRole => roles.includes(requiredRole)))
		);
	}

	onMenuButtonClick(entry: PageHeaderMenuEntry) {
		if (entry.route) {
			this.navigateTo(entry.route);
			return;
		}
		if (entry.url) {
			if (entry.url.startsWith("mailto")) {
				window.location.href = entry.url;
				return;
			}
			window.open(entry.url, "_blank");
		}
	}
}
